<template>
    <Card pageTitle="Loyalty Reconciliation">
        <div v-if="canView">
            <div class="reconciliation-wrap">
                <div class="page-head">
                    <h2>Reconciliation Issues</h2>
                    <p>Review loyalty exceptions from earning, refund clawback, redemption, and expiry jobs.</p>
                    <p v-if="!canEdit" class="muted read-only-note">Read-only: ask a loyalty administrator to add notes or resolve issues.</p>
                </div>

                <div class="toolbar">
                    <select v-model="resolvedFilter" class="native-select" @change="loadIssues">
                        <option value="0">Open</option>
                        <option value="1">Resolved</option>
                    </select>
                    <Button borderOnly @click="loadIssues">Refresh</Button>
                </div>

                <table class="issues-table" v-if="issues.length">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Customer</th>
                            <th>Invoice / Reference</th>
                            <th>Unrecovered</th>
                            <th>Error</th>
                            <th>Status</th>
                            <th>Notes / History</th>
                            <th v-if="resolvedFilter === '1'">Resolved</th>
                            <th v-if="resolvedFilter === '1'">Resolution note</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="issue in issues" :key="issue.id">
                            <td>{{ issue.type }}</td>
                            <td :title="issue.customer_id || ''">{{ issue.customer_name || issue.customer_id || "-" }}</td>
                            <td :title="issue.ref_id || ''">{{ issue.invoice_number || issue.ref_id || "-" }}</td>
                            <td>{{ issue.unrecovered_points || 0 }}</td>
                            <td>{{ issue.error || "-" }}</td>
                            <td>
                                <span :class="['status-badge', issue.resolved_at ? 'neutral' : 'warn']">
                                    {{ issue.resolved_at ? "Resolved" : "Open" }}
                                </span>
                            </td>
                            <td>
                                <div v-if="issue.history && issue.history.length" class="history-list">
                                    <div v-for="entry in issue.history" :key="`${issue.id}-${entry.at}-${entry.action}`">
                                        <strong>{{ entry.action }}</strong>
                                        <span>{{ formatDate(entry.at) }}</span>
                                        <small>{{ entry.user_name || entry.user_id || "System" }}</small>
                                        <p v-if="entry.note">{{ entry.note }}</p>
                                    </div>
                                </div>
                                <span v-else class="muted">No notes yet</span>
                            </td>
                            <td v-if="resolvedFilter === '1'">{{ formatDate(issue.resolved_at) }}</td>
                            <td v-if="resolvedFilter === '1'">{{ issue.resolved_note || "-" }}</td>
                            <td>
                                <div v-if="canEdit" class="resolve-cell">
                                    <CustomInput v-model="issue.work_note" :placeholder="issue.resolved_at ? 'Add follow-up note' : 'Note or resolution'" />
                                    <Button borderOnly @click="addNote(issue)">Add note</Button>
                                    <Button v-if="!issue.resolved_at" @click="resolveIssue(issue)">Resolve</Button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-else class="empty-state">
                    <strong>No {{ resolvedFilter === "0" ? "open" : "resolved" }} reconciliation issues</strong>
                    <span>Exceptions from refunds, clawbacks, and expiry jobs will appear here when they need review.</span>
                </div>
            </div>
        </div>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, canViewLoyaltyReconciliation, PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { errMsg } from "../../utils/common"

export default {
    components: { Card, Button, CustomInput },
    data() {
        return {
            PERMISSION_CODES,
            resolvedFilter: "0",
            issues: [],
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canView() {
            return canViewLoyaltyReconciliation(this.user)
        },
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_LOYALTY,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
    },
    async mounted() {
        if (this.canView) await this.loadIssues()
    },
    methods: {
        async loadIssues() {
            try {
                const res = await LoyaltyFetchApi.fetchReconciliationIssues(this.resolvedFilter)
                this.issues = (res.result || []).map((issue) => ({ ...issue, work_note: "" }))
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load reconciliation issues"))
            }
        },
        async resolveIssue(issue) {
            try {
                const note = String(issue.work_note || "").trim()
                if (!note) {
                    this.$toast.error("A resolution note is required")
                    return
                }
                await LoyaltyFetchApi.resolveReconciliationIssue(issue.id, { note })
                await this.loadIssues()
                this.$toast.success("Issue resolved")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not resolve issue"))
            }
        },
        async addNote(issue) {
            const note = String(issue.work_note || "").trim()
            if (!note) {
                this.$toast.error("Enter a note first")
                return
            }
            try {
                await LoyaltyFetchApi.addReconciliationIssueNote(issue.id, { note })
                await this.loadIssues()
                this.$toast.success("Note added")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not add note"))
            }
        },
        formatDate(value) {
            if (!value) return "-"
            return new Date(value).toLocaleString()
        },
    },
}
</script>

<style lang="scss" scoped>
.reconciliation-wrap { max-width: 1200px; }
.page-head { margin-bottom: 20px; }
.page-head h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: #0b302c; }
.page-head p { margin: 0; color: #5f6368; line-height: 1.45; }
.page-head .read-only-note { margin-top: 6px; font-size: 13px; }
.toolbar { display: flex; gap: 12px; align-items: center; margin-bottom: 16px; }
.native-select { min-height: 40px; border: 1px solid #d9dee3; border-radius: 6px; padding: 8px; background: #fff; }
.issues-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.issues-table th, .issues-table td { text-align: left; padding: 9px 8px; border-bottom: 1px solid #eee; vertical-align: top; overflow-wrap: anywhere; }
.issues-table th { white-space: nowrap; }
.issues-table th:nth-child(4), .issues-table td:nth-child(4) { width: 92px; }
.issues-table th:nth-child(6), .issues-table td:nth-child(6) { width: 86px; }
.issues-table th:nth-child(7), .issues-table td:nth-child(7) { width: 140px; }
.resolve-cell { display: flex; gap: 8px; min-width: 260px; }
.muted { color: #80868b; }
.history-list { display: grid; gap: 8px; min-width: 220px; }
.history-list > div { border-left: 3px solid #d5e4e1; padding-left: 8px; color: #374151; }
.history-list strong, .history-list span, .history-list small { display: block; }
.history-list p { margin: 4px 0 0; color: #4b5563; }
.status-badge { display: inline-flex; align-items: center; border-radius: 999px; padding: 3px 8px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.status-badge.neutral { background: #eef0f2; color: #4b5563; }
.status-badge.warn { background: #fff3d6; color: #9a5b00; }
.empty-state { min-height: 180px; display: flex; flex-direction: column; justify-content: center; gap: 6px; color: #6b7280; border-top: 1px solid #edf0f2; }
.empty-state strong { color: #111827; font-size: 16px; }
</style>
